import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { UpdateExpertSlugResponse } from "@shopexperts/legacy-api";

export interface UpdateExpertSlugVariables {
  expertId: number;
  slug: string;
  createRedirect: boolean;
  /** The slug being replaced, or null when the expert had no public URL yet. */
  previousSlug: string | null;
}

/**
 * Pulls the message the admin needs out of a failed slug update. Laravel sends
 * field errors for the validation rules (taken slug, bad characters) and a
 * plain message for the domain rules (same slug, redirect already exists).
 */
export function extractSlugErrorMessage(error: unknown): string {
  const apiError = error as {
    response?: { data?: { message?: string; errors?: Record<string, string[]> } };
    message?: string;
  };
  const fieldError = apiError?.response?.data?.errors?.slug?.[0];
  if (fieldError) return fieldError;
  return (
    apiError?.response?.data?.message ??
    apiError?.message ??
    "Could not change the profile URL. Try again."
  );
}

/**
 * Only warn about broken links when there was a public URL to break — an expert
 * getting its first slug has no old address for anyone to have linked to.
 */
export function slugChangeDescription(
  redirect: UpdateExpertSlugResponse["redirect"],
  previousSlug: string | null,
): string {
  if (redirect) {
    return `${redirect.source_path} now redirects to ${redirect.target_path}.`;
  }
  return previousSlug
    ? "No redirect was created — links to the old URL will 404."
    : "The profile is now published at its new URL.";
}

export function useExpertSlugUpdate() {
  const api = useApi();
  const queryClient = useQueryClient();
  const toaster = useToaster();

  const updateSlug = useMutation({
    mutationFn: async ({ expertId, slug, createRedirect }: UpdateExpertSlugVariables) => {
      return await api.admin.expertManager.updateSlug({
        params: { expertId },
        body: { slug, create_redirect: createRedirect },
      });
    },
    onSuccess: (data, variables) => {
      toaster.create({
        title: "Profile URL changed",
        description: slugChangeDescription(data.redirect, variables.previousSlug),
        type: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "experts"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "expert"] });
    },
  });

  return { updateSlug };
}
